<template>
  <div>
    <div class="overflow-x-auto overflow-y-auto pt-2">
      <v-data-table
        :headers="headers"
        :items="analytics"
        class="table-radius border border-[#e0e0e0]"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        fixed-header
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="`tooltip-${index}`"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="`normal-${index}`" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template v-slot:item="{ item }">
          <tr>
            <td v-for="(column, index) in headers" :key="index" class="px-4 py-6 font-semibold">
              {{ item[column.value] }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import {
  fullName,
  hasPermission,
  mergeOptions,
  mergeOptionsDetails,
  debounce
} from '@/utils/common'
import permissionData from '@/utils/permissions'
export default {
  props: {
    caseConfig: Object,
    employees: Array,
    // Adding props for filter values from parent
    filterEmployee: String,
    startDate: String,
    endDate: String
  },
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      headers: [],
      analytics: [],
      unAttended: 'UN-ATTENDED/IN-COMPLETE',
      options: { sortBy: [], sortDesc: [], itemsPerPage: 10, page: 1 }
    }
  },
  computed: {
    users() {
      return this.employees.map((emp) => ({
        ...emp,
        fullName: fullName(emp)
      }))
    },
    canEdit() {
      return hasPermission(this.userData, permissionData.collectionEdit)
    },
  },
  watch: {
    '$route.query.empId': {
      async handler(empId) {
        try {
          // Using prop instead of local data
          // this.filterEmployee = empId || ''
          this.options.page = 1
          await this.debouncedFetchCaseAnalytics()
        } catch (error) {
          console.log(error)
        }
      },
      immediate: true
    },
    caseConfig: {
      handler(config) {
        if (config) {
          const fm = mergeOptions(config.userDefinedCaseStatus)
          const mergedOptionsDetails = mergeOptionsDetails(config.userDefinedCaseStatus)
          this.fetchCaseAnalytics()
          this.headers = [
            {
              text: 'Employees Name',
              value: 'empName',
              sortable: false
            }
          ]

          fm.forEach((status) => {
            if (!this.headers.some((header) => header.value === status)) {
              this.headers.push({
                text: status,
                value: status,
                sortable: false
              })
            }
          })
          this.headers.push({
            text: this.unAttended,
            value: this.unAttended,
            sortable: false
          })
          this.headers.push({
            text: 'Grand Total',
            value: 'total',
            sortable: false
          })

          const filteredOptions = mergeOptionsDetails(this.caseConfig.userDefinedCaseStatus).filter(
            (el) => el.fields.some((f) => f.name === 'amount' && f.visibility === true)
          )

          const Columns = filteredOptions.map((option) => ({
            text: `${option.code} Amt.`,
            value: `${option.code}_amount`,
            key: `${option.code}`,
            width: '100px',
            sortable: false
          }))
          this.headers.push(...Columns)
        }
      },
      immediate: true
    },
    // Add watchers for the filter props
    filterEmployee: {
      handler() {
        this.fetchCaseAnalytics()
      }
    },
    startDate: {
      handler() {
        this.fetchCaseAnalytics()
      }
    },
    endDate: {
      handler() {
        this.fetchCaseAnalytics()
      }
    }
  },
  methods: {
    async fetchCaseAnalytics() {
      try {
        const params = {
          caseType: this.caseConfig?.name
          // subCase: '68dc12de52196fb2cc393c64'
        }
        // Using props instead of local data
        if (this.startDate && this.endDate) {
          params.start = this.startDate
          params.end = this.endDate
        }
        if (this.filterEmployee) {
          params.search = this.filterEmployee
        }

        const response = await this.$axios.get('/collection/cases/analytics', {
          params
        })

        // Ensure userCollections is structured correctly
        const userCollections = response.data?.caseAnalytics.userCollectionStats.reduce(
          (collections, el) => {
            if (!collections[el.userId]) {
              collections[el.userId] = []
            }
            // collections[el.userId] = collections[el.userId].concat(el.fundsByOutcome || [])
            collections[el.userId] = collections[el.userId].concat(
              { outcome: el.userDefinedCaseStatus, amount: el.totalFundCollected } || []
            )

            return collections
          },
          {}
        )

        const data = response.data?.caseAnalytics.userDefinedAnalytics
          .filter((el) => !this.filterEmployee || el._id === this.filterEmployee)
          .map((el) => {
            const allStatuses = el.statuses.reduce(
              (acc, { status, totalCount }) => {
                if (status) {
                  acc[status] = totalCount
                  acc[this.unAttended] += totalCount
                }
                return acc
              },
              { [this.unAttended]: 0 }
            )

            allStatuses[this.unAttended] = el.globalCount - allStatuses[this.unAttended]

            mergeOptions(this.caseConfig.userDefinedCaseStatus).forEach((status) => {
              if (!allStatuses[status]) {
                allStatuses[status] = 0
              }
            })

            const filteredOptions = mergeOptionsDetails(
              this.caseConfig.userDefinedCaseStatus
            ).filter((el) => el.fields.some((f) => f.name === 'amount' && f.visibility === true))

            const amountCollected = filteredOptions.reduce((acc, option) => {
              const userCollection = userCollections[el._id] || []
              const matchedOutcomes = userCollection.filter(
                (fund) =>
                  fund.outcome &&
                  String(fund.outcome).trim().toLowerCase() ===
                    String(option.code).trim().toLowerCase()
              )

              const totalAmount = matchedOutcomes.reduce((sum, fund) => sum + (fund.amount || 0), 0)

              acc[`${option.code}_amount`] = totalAmount ?? 0
              return acc
            }, {})

            // Construct userStatus
            const userStatus = {
              empName: el.assignedTo,
              total: el.globalCount,
              ...amountCollected,
              ...allStatuses
            }

            if (!userStatus.empName || userStatus.empName.trim() === '') {
              return null
            }

            return userStatus
          })
          .filter((userStatus) => userStatus !== null)

        this.analytics = data
      } catch (error) {
        console.error('Error in fetchCaseAnalytics:', error)
      }
    },

    // Removing methods that are now handled by the parent component
    /*
    onDateSelected() {
      if (this.dates.length === 2) {
        this.startDate = this.formatDate(this.dates[0])
        this.endDate = this.formatDate(this.dates[1])
        this.fetchCaseAnalytics()
      } else {
        this.startDate = this.formatDate(this.dates[0])
        this.endDate = null
      }
    },
    formatDate(dateString) {
      return this.$dayjs(dateString).format('YYYY-MM-DD')
    },
    visibleDate(date) {
      return this.$dayjs(date).format('DD-MM-YYYY')
    },
    */
    exportToXLSX() {
      const dataToExport = this.analytics.map((item) => {
        const exportItem = {}
        this.headers.forEach((header) => {
          exportItem[header.text] = item[header.value]
        })
        return exportItem
      })
      const XLSX = require('xlsx')
      const ws = XLSX.utils.json_to_sheet(dataToExport)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Case Analytics')

      XLSX.utils.sheet_add_aoa(ws, [this.headers.map((header) => header.text)], { origin: 'A1' })

      const currentDate = this.$dayjs().format('DD/MM/YYYY')
      XLSX.writeFile(wb, `${this.caseConfig.name}_Case_Analytics_${currentDate}.xlsx`)
    },
    handleInput() {
      this.fetchCaseAnalytics()
    }
    /*
    onDateCleared() {
      this.dates = []
      this.startDate = null
      this.endDate = null
      this.fetchCaseAnalytics()
    }
    */
  },
  mounted() {},
  beforeCreate() {
    this.debouncedFetchCaseAnalytics = () => {}
  },
  created() {
    this.debouncedFetchCaseAnalytics = debounce(this.fetchCaseAnalytics, 800)
  }
}
</script>

<style scoped>
.wfm-search-fields.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 40px !important;
}
/* .v-data-table >>> .v-data-table__wrapper > table > thead > tr > th {
  font-size: 0.875rem;
  color: #000;
} */
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
